import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { current } from "immer";
import type { OfferDetailResponse } from "@codway-dev/shared";
import { OfferService } from "../services/offer.service";
import { isDeepEqual } from "../utils/deep-equal";
import {
  DEFAULT_DOWNSELL_EDITOR_STATE,
  offerToState,
  stateToCreateRequest,
  stateToUpdateRequest,
  type DownsellEditorState,
} from "./downsell-editor.mapping";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DownsellEditorStoreState {
  config: DownsellEditorState;
  offerId: string | null;
  originalConfig: OfferDetailResponse["config"] | null;
  initialConfig: DownsellEditorState | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface DownsellEditorActions {
  initNew: () => void;
  loadFromBackend: (id: string) => Promise<void>;
  updateField: <K extends keyof DownsellEditorState>(
    key: K,
    value: DownsellEditorState[K]
  ) => void;
  save: () => Promise<string>;
  discardChanges: () => void;
  reset: () => void;
}

type DownsellEditorStore = DownsellEditorStoreState & DownsellEditorActions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectDownsellIsDirty = (state: DownsellEditorStore) => {
  if (state.initialConfig === null) return false;
  return !isDeepEqual(state.config, state.initialConfig);
};

// ─── Store ────────────────────────────────────────────────────────────────────

const INITIAL_STATE: DownsellEditorStoreState = {
  config: DEFAULT_DOWNSELL_EDITOR_STATE,
  offerId: null,
  originalConfig: null,
  initialConfig: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

export const useDownsellEditorStore = create<DownsellEditorStore>()(
  devtools(
    immer((set, get) => ({
      ...INITIAL_STATE,

      initNew: () =>
        set((state) => {
          state.config = DEFAULT_DOWNSELL_EDITOR_STATE;
          state.offerId = null;
          state.originalConfig = null;
          state.initialConfig = DEFAULT_DOWNSELL_EDITOR_STATE;
          state.isLoading = false;
          state.isSaving = false;
          state.error = null;
        }),

      loadFromBackend: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const offer = await OfferService.get(id);
          const hydrated = offerToState(offer);
          set((state) => {
            state.config = hydrated;
            state.offerId = offer.id;
            state.originalConfig = offer.config;
            state.initialConfig = hydrated;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error
                ? err.message
                : "No se pudo cargar el downsell";
            state.isLoading = false;
          });
          throw err;
        }
      },

      updateField: (key, value) =>
        set((state) => {
          Object.assign(state.config, { [key]: value });
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const s = get();
          let savedId: string;

          if (s.offerId === null) {
            const created = await OfferService.create(
              stateToCreateRequest(s.config)
            );
            savedId = created.id;
          } else {
            const updated = await OfferService.update(
              s.offerId,
              stateToUpdateRequest(s.config, s.originalConfig)
            );
            savedId = updated.id;
          }

          set((state) => {
            state.offerId = savedId;
            state.initialConfig = current(state.config);
            state.isSaving = false;
          });

          return savedId;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Error al guardar el downsell";
          set((state) => {
            state.isSaving = false;
            state.error = message;
          });
          throw new Error(message);
        }
      },

      discardChanges: () =>
        set((state) => {
          if (!state.initialConfig) return;
          state.config = state.initialConfig;
        }),

      reset: () =>
        set((state) => {
          Object.assign(state, INITIAL_STATE);
        }),
    })),
    { name: "DownsellEditorStore" }
  )
);
